import PopupState, { bindMenu, bindTrigger } from 'material-ui-popup-state'
import React, { useState } from 'react'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  IconButton,
  ListItemIcon,
  ListItemText,
  Switch,
  useTheme,
} from '@mui/material'
import { BorderColor, Delete, MoreVert } from '@mui/icons-material'
import { StyledMenu } from '../../styles/PopupStyles'
import MenuItem from '@mui/material/MenuItem'
import { useNotification } from '../../store/redux/hooks'
import { t } from 'i18next'
import { Trans } from 'react-i18next'
import {
  useChangeDictionaryInactivityMutation,
  useDeleteDictionaryMutation,
} from '../../store/redux/reducers/dictionarySlice'
import AddEditDictionaryDialog from '../../views/TypographySettingsView/AddEditDictionaryDialog'
import Dictionary from '../../store/types/models/Dictionary'
import ConfirmButton from '../Button/ConfirmButton'

type DictionariesActionMenuProps = {
  dictionary: Dictionary
}

const DictionariesActionMenu = ({ dictionary }: DictionariesActionMenuProps) => {
  const dictionaryId = dictionary.id
  const theme = useTheme()
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false)
  const [deleteDictionary] = useDeleteDictionaryMutation()
  const { displayNotification } = useNotification()
  const [open, setOpen] = useState(false)
  const [changeInactive] = useChangeDictionaryInactivityMutation()
  const [inactive, setInactive] = useState(dictionary.inactive)
  const [loading, setLoading] = useState(false)

  const handleDeleteDictionary = async (popupState: any) => {
    if (dictionaryId === null) {
      return
    }
    try {
      await deleteDictionary(dictionaryId).unwrap()
      displayNotification({ message: t('page.dictionarySettings.alert.dictionaryDeleted'), type: 'success' })
    } catch (_) {
      displayNotification({
        message: t('page.dictionarySettings.alert.dictionaryDeleteError'),
        type: 'error',
      })
    }
    setOpenDeleteDialog(false)
    popupState.close()
  }

  const handleEditClick = () => {
    setOpen(true)
  }

  const handleChangeInactive = async (popupState: any) => {
    if (loading) return
    setLoading(true)
    setInactive(!inactive)
    if (dictionaryId === null) {
      return
    }
    try {
      await changeInactive(dictionaryId).unwrap()
      displayNotification({
        message: !dictionary.inactive
          ? t('page.dictionarySettings.alert.dictionaryChangedInactive')
          : t('page.dictionarySettings.alert.dictionaryChangedActive'),
        type: 'success',
      })
    } catch (_) {
      displayNotification({
        message: t('page.dictionarySettings.alert.dictionaryChangeInactiveError'),
        type: 'error',
      })
    }
    popupState.close()
  }

  return (
    <PopupState
      popupId={`dictionary-action-menu-${dictionaryId}`}
      variant={'popover'}
      parentPopupState={null}
      disableAutoFocus={null}
    >
      {popupState => (
        <React.Fragment>
          <IconButton
            {...bindTrigger(popupState)}
            aria-label={t('page.dictionarySettings.aria.moreOptions')}
            sx={{ color: 'inherit' }}
          >
            <MoreVert />
          </IconButton>
          <StyledMenu sx={{ '& .MuiPaper-root': { minWidth: '50px' } }} {...bindMenu(popupState)}>
            <MenuItem onClick={handleEditClick}>
              <ListItemIcon>
                <BorderColor />
              </ListItemIcon>
              <ListItemText>{t('page.dictionarySettings.dialog.editText')}</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => setOpenDeleteDialog(true)}>
              <ListItemIcon>
                <Delete />
              </ListItemIcon>
              <ListItemText>{t('page.dictionarySettings.dialog.deleteText')}</ListItemText>
            </MenuItem>
            <MenuItem
              disableRipple
              sx={{ color: 'text.main' }}
              onClick={() => {
                handleChangeInactive(popupState)
              }}
              disabled={loading}
            >
              <FormControlLabel
                onClick={e => {
                  e.preventDefault()
                }}
                control={<Switch size="small" checked={inactive} sx={{ mr: 1 }} />}
                label={t('components.toggle.inactive')}
              />
            </MenuItem>
          </StyledMenu>
          <AddEditDictionaryDialog
            open={open}
            setOpen={setOpen}
            editedDictionary={dictionary}
            actionMenuPopupState={popupState}
          />
          <Dialog
            onClose={() => {
              setOpenDeleteDialog(false)
            }}
            open={openDeleteDialog}
            PaperProps={{
              style: {
                paddingLeft: theme.spacing(1),
                paddingRight: theme.spacing(2),
                paddingTop: theme.spacing(1),
                paddingBottom: theme.spacing(2),
              },
            }}
          >
            <DialogTitle>{t('page.dictionarySettings.dialog.delete.title')}</DialogTitle>
            <DialogContent>
              <DialogContentText>
                <Trans i18nKey="page.dictionarySettings.dialog.delete.content" values={{ name: dictionaryId }} />
              </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ gap: 2 }}>
              <Button size="small" onClick={() => setOpenDeleteDialog(false)}>
                {t('page.adminsPage.dialog.return')}
              </Button>
              <ConfirmButton
                text={t('page.projects.dialog.delete.confirm')}
                onClick={() => handleDeleteDictionary(popupState)}
              />
            </DialogActions>
          </Dialog>
        </React.Fragment>
      )}
    </PopupState>
  )
}

export default DictionariesActionMenu